import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/AppShell";
import { PageHeader, Panel, Stat, StatGrid } from "@/components/primitives";
import { useNow, useYard } from "@/lib/use-yard";
import { fmtDuration, waitMinutes } from "@/lib/yard-data";

export const Route = createFileRoute("/yard")({
  head: () => ({
    meta: [
      { title: "Yard Map — PortDwellr" },
      { name: "description", content: "Every dock door and staging spot, occupied or open, with the container currently sitting in it." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: () => (
    <AppShell>
      <YardMapPage />
    </AppShell>
  ),
});

function YardMapPage() {
  const { spots, trucks } = useYard();
  const now = useNow(2000);
  const byId = new Map(trucks.map((t) => [t.id, t]));

  const docks = spots.filter((s) => s.kind === "dock");
  const staging = spots.filter((s) => s.kind === "staging");
  const occupied = spots.filter((s) => s.occupantId).length;

  const grid = (list: typeof spots) => (
    <div className="grid grid-cols-2 border-l border-t border-line sm:grid-cols-3 lg:grid-cols-4">
      {list.map((s) => {
        const t = s.occupantId ? byId.get(s.occupantId) : undefined;
        return (
          <div
            key={s.id}
            className={`min-h-[128px] border-b border-r border-line p-4 ${t ? "bg-steel text-paper" : "bg-paper-raised"}`}
          >
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
              <span className="min-w-0 break-all font-mono text-[16px] font-semibold">{s.label}</span>
              <span
                className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] ${t ? "text-signal" : "text-steel-mid"}`}
              >
                {t ? "Occupied" : "Open"}
              </span>
            </div>
            {t ? (
              <div className="mt-3 font-mono text-[11.5px] leading-relaxed text-paper/80">
                <div className="text-paper">{t.container}</div>
                <div>{t.carrier}</div>
                <div>Truck {t.queueNo} · {t.trailerType}</div>
                <div className="mt-1 text-signal">on spot {fmtDuration(waitMinutes(t, now))}</div>
              </div>
            ) : (
              <div className="mt-3 font-mono text-[11.5px] text-steel-mid">Available for ASSIGN</div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="ASSIGN · space allocation"
        title="Yard Map"
        lead="Dock doors and staging spots as ASSIGN currently holds them. When a door frees up, the next truck by priority score is called from the sequence queue automatically."
      />

      <StatGrid>
        <Stat value={`${occupied}/${spots.length}`} label="Spots occupied" sub="docks + staging" />
        <Stat value={docks.filter((d) => !d.occupantId).length} label="Dock doors open" sub="ready to call" />
        <Stat value={staging.filter((s) => !s.occupantId).length} label="Staging spots open" sub="overflow capacity" />
      </StatGrid>

      <div className="mt-10 space-y-10">
        <Panel title="Dock doors">{grid(docks)}</Panel>
        <Panel title="Staging spots">{grid(staging)}</Panel>
      </div>
    </>
  );
}
